import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { getGenreByKey } from '@/lib/data';
import DramaCard from '@/components/feature/DramaCard';

const YEAR_OPTIONS = ['不限', '2026', '2025', '2024'];
const RATING_OPTIONS = ['不限', '9分以上', '8分以上', '7分以上'];
const STATUS_OPTIONS = ['全部', '更新中', '已完结'];
const SORT_OPTIONS = [
  { key: 'newest', label: '最新上线' },
  { key: 'heat', label: '最热人气' },
  { key: 'rating', label: '评分最高' },
];
const PAGE_SIZE = 8;

export default function Category() {
  const { genre } = useParams();
  const [, setSearchParams] = useSearchParams();
  const genreMeta = getGenreByKey(genre);
  const genreName = genreMeta?.name ?? genre ?? '';

  const [year, setYear] = useState('不限');
  const [rating, setRating] = useState('不限');
  const [status, setStatus] = useState('全部');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setYear('不限');
    setRating('不限');
    setStatus('全部');
    setSort('newest');
    setPage(1);
    setFiltersOpen(false);
  }, [genre]);

  const minRating = rating === '不限' ? 0 : Number(rating.replace('分以上', ''));

  const filtered = useMemo(() => {
    let list = dramas.filter((d) => d.genres.includes(genreName));
    if (year !== '不限') list = list.filter((d) => String(d.year) === year);
    if (minRating > 0) list = list.filter((d) => d.rating >= minRating);
    if (status !== '全部') list = list.filter((d) => d.status === status);

    if (sort === 'heat') list = [...list].sort((a, b) => b.heat - a.heat);
    else if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    else list = [...list].sort((a, b) => b.year - a.year || b.heat - a.heat);
    return list;
  }, [genreName, year, minRating, status, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const resetFilters = () => {
    setYear('不限');
    setRating('不限');
    setStatus('全部');
    setSort('newest');
    setPage(1);
    setSearchParams({});
  };

  const hasActiveFilter = year !== '不限' || rating !== '不限' || status !== '全部';

  const chips = (
    <div className="space-y-4">
      <FilterRow label="上映年份" options={YEAR_OPTIONS} value={year} onChange={(v) => { setYear(v); setPage(1); }} />
      <FilterRow label="评分区间" options={RATING_OPTIONS} value={rating} onChange={(v) => { setRating(v); setPage(1); }} />
      <FilterRow label="播出状态" options={STATUS_OPTIONS} value={status} onChange={(v) => { setStatus(v); setPage(1); }} />
    </div>
  );

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{genreName}</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
            {genreName}类韩剧
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            {genreMeta?.desc ?? '浏览该题材下的热门韩剧，可按年份、评分与播出状态筛选。'}
          </p>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-6">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部剧集
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFiltersOpen((v) => !v)}
                className="lg:hidden cursor-pointer whitespace-nowrap flex items-center gap-1 text-sm border border-background-300 rounded-full px-3.5 py-2 text-foreground-700"
              >
                <i className="ri-filter-3-line"></i> 筛选
              </button>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => { setSort(e.target.value); setPage(1); }}
                  className="cursor-pointer appearance-none bg-background-100 border border-background-200 rounded-full text-sm text-foreground-700 pl-4 pr-9 py-2 outline-none focus:border-primary-400"
                >
                  {SORT_OPTIONS.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-500"></i>
              </div>
            </div>
          </div>

          <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block mt-4 pt-4 border-t border-background-200`}>
            {chips}
            {hasActiveFilter && (
              <button
                onClick={resetFilters}
                className="mt-4 cursor-pointer whitespace-nowrap inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <i className="ri-refresh-line"></i> 重置筛选条件
              </button>
            )}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
              {pageItems.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={current === 1}
                  className="cursor-pointer whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`cursor-pointer w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                      n === current ? 'bg-primary-500 text-white' : 'border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={current === totalPages}
                  className="cursor-pointer w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-100 py-20 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground-900">没有符合筛选条件的剧集</h3>
            <p className="mt-2 text-sm text-foreground-500">试试放宽筛选条件，或查看该题材的全部剧集。</p>
            <button
              onClick={resetFilters}
              className="mt-5 cursor-pointer whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface FilterRowProps {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}

function FilterRow({ label, options, value, onChange }: FilterRowProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <span className="text-sm text-foreground-500 w-20 shrink-0">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`cursor-pointer whitespace-nowrap text-sm px-3.5 py-1.5 rounded-full transition-colors ${
              value === opt
                ? 'bg-secondary-900 text-white'
                : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}